import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const LOGO = '/dailyreps3.png';
const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Unsubscribe() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [categoryLabel, setCategoryLabel] = useState('');
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [unsubscribedAll, setUnsubscribedAll] = useState(false);
  const [error, setError] = useState('');
  const [allLoading, setAllLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function processUnsubscribe() {
      try {
        // First, validate the token and get category info
        const validateRes = await fetch(`${API_BASE}/api/email/unsubscribe/${token}`);
        const validateData = await validateRes.json();

        if (cancelled) return;

        if (!validateRes.ok) {
          setError(validateData.error || 'This unsubscribe link is invalid or has expired.');
          setLoading(false);
          return;
        }

        setCategoryLabel(validateData.category_label || 'this category');

        // Auto-submit the single-category unsubscribe
        const unsubRes = await fetch(`${API_BASE}/api/email/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (cancelled) return;

        if (!unsubRes.ok) {
          const unsubData = await unsubRes.json().catch(() => ({}));
          setError(unsubData.error || 'Failed to unsubscribe. Please try again.');
        } else {
          setUnsubscribed(true);
        }
      } catch {
        if (!cancelled) {
          setError('Something went wrong. Please try again later.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    processUnsubscribe();
    return () => { cancelled = true; };
  }, [token]);

  const handleUnsubscribeAll = async () => {
    setAllLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/email/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, unsubscribe_all: true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to unsubscribe from all emails.');
      } else {
        setUnsubscribedAll(true);
      }
    } catch {
      setError('Something went wrong. Please try again later.');
    } finally {
      setAllLoading(false);
    }
  };

  const containerStyle = {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    minHeight: '100vh',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    color: '#222',
  };

  const cardStyle = {
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <img src={LOGO} alt="Train Daily" style={{ width: 60, height: 60, marginBottom: 24 }} />
        <div style={cardStyle}>
          <div className="loading"><div className="spinner" /></div>
        </div>
      </div>
    );
  }

  if (error && !unsubscribed) {
    return (
      <div style={containerStyle}>
        <img src={LOGO} alt="Train Daily" style={{ width: 60, height: 60, marginBottom: 24 }} />
        <div style={cardStyle}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 16, color: '#222' }}>Unsubscribe</h1>
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '16px 20px',
            color: '#b91c1c',
            fontSize: '0.95rem',
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <img src={LOGO} alt="Train Daily" style={{ width: 60, height: 60, marginBottom: 24 }} />
      <div style={cardStyle}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24, color: '#222' }}>Unsubscribe</h1>

        {/* Success checkmark */}
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#dcfce7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: '1.6rem',
          color: '#16a34a',
        }}>
          &#10003;
        </div>

        {unsubscribedAll ? (
          <p style={{ fontSize: '1rem', color: '#444', marginBottom: 32, lineHeight: 1.5 }}>
            You've been unsubscribed from all optional emails from Train Daily.
          </p>
        ) : (
          <p style={{ fontSize: '1rem', color: '#444', marginBottom: 32, lineHeight: 1.5 }}>
            You have been unsubscribed from: <strong>{categoryLabel}</strong>
          </p>
        )}

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '12px 16px',
            color: '#b91c1c',
            fontSize: '0.85rem',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {!unsubscribedAll && (
          <button
            onClick={handleUnsubscribeAll}
            disabled={allLoading}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: allLoading ? '#ccc' : '#f77c00',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1rem',
              fontWeight: 600,
              cursor: allLoading ? 'not-allowed' : 'pointer',
              marginBottom: 16,
            }}
          >
            {allLoading ? 'Processing...' : 'Turn off all emails'}
          </button>
        )}

        <a
          href="/login"
          style={{
            display: 'inline-block',
            color: '#1348e5',
            fontSize: '0.9rem',
            textDecoration: 'none',
            marginTop: 8,
          }}
        >
          Manage all communication preferences
        </a>

        <p style={{ fontSize: '0.8rem', color: '#999', marginTop: 32 }}>
          Required emails (account setup and security) will always be sent.
        </p>
      </div>
    </div>
  );
}
